export type ActiveFilterChipKind = 'price' | 'school' | 'moulding' | 'mat' | 'frameType' | 'graduationYear';

export type ActiveFilterChip = {
    id: string;
    kind: ActiveFilterChipKind;
    value: string;
    label: string;
    removeName: string;
};

export type ActiveFilterChipInput = {
    schoolNoun: string;
    appliedSchool: string;
    priceMin: number;
    priceMax: number;
    defaultPriceMin: number;
    defaultPriceMax: number;
    mouldings: readonly string[];
    mats: readonly string[];
    frameTypes: readonly string[];
    baselineFrameTypes: readonly string[];
    graduationYear: string;
    frameTypeLabels: Readonly<Record<string, string>>;
};

/** Noun used on the school chip. Portal 3 matches the visible "school/state" heading. */
export function schoolFilterNoun(portalId: number | undefined): string {
    if (portalId === 1) {
        return 'organization';
    }

    if (portalId === 2) {
        return 'Military Force';
    }

    if (portalId === 3) {
        return 'school/state';
    }

    return 'school';
}

export const ACTIVE_FILTER_FRAME_TYPE_LABELS: Readonly<Record<string, string>> = {
    tassel: 'Tassel Frame',
    diploma: 'Diploma Frame',
    certificate: 'Certificate Frame',
};

export function formatActivePrice(min: number, max: number): string {
    return `$${min} to $${max}`;
}

function chip(kind: ActiveFilterChipKind, id: string, group: string, value: string, display = value): ActiveFilterChip {
    return {
        id,
        kind,
        value,
        label: `${group}: ${display}`,
        removeName: `Remove ${group}: ${display}.`,
    };
}

function uniqueNonBlank(values: readonly string[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];

    for (const value of values) {
        if (value === '' || seen.has(value)) {
            continue;
        }

        seen.add(value);
        result.push(value);
    }

    return result;
}

/**
 * Customer selections only. Blank "All" sentinels, the default price range,
 * and frame types that came from the page prefilter are omitted.
 */
export function listActiveFilterChips(input: ActiveFilterChipInput): ActiveFilterChip[] {
    const chips: ActiveFilterChip[] = [];

    if (input.priceMin !== input.defaultPriceMin || input.priceMax !== input.defaultPriceMax) {
        chips.push(chip('price', 'price', 'price', formatActivePrice(input.priceMin, input.priceMax)));
    }

    if (input.appliedSchool !== '') {
        chips.push(chip('school', 'school', input.schoolNoun, input.appliedSchool));
    }

    for (const moulding of uniqueNonBlank(input.mouldings)) {
        chips.push(chip('moulding', `moulding:${moulding}`, 'moulding', moulding));
    }

    for (const mat of uniqueNonBlank(input.mats)) {
        chips.push(chip('mat', `mat:${mat}`, 'mat', mat));
    }

    const baselineFrameTypes = new Set(input.baselineFrameTypes);

    for (const frameType of uniqueNonBlank(input.frameTypes)) {
        if (baselineFrameTypes.has(frameType)) {
            continue;
        }

        const name = input.frameTypeLabels[frameType] ?? frameType;
        chips.push(chip('frameType', `frame:${frameType}`, 'frame type', frameType, name));
    }

    if (input.graduationYear !== '') {
        chips.push(chip('graduationYear', 'graduation-year', 'graduation year', input.graduationYear));
    }

    return chips;
}
